// src/features/unitsSlice.js

import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import axiosInstance from '../axiosInstance';
import { logout } from './authSlice';
import { selectUserData } from './userSlice'; // Import selector to get user data
import {
    cacheUnitsData,
    getCachedUnitsData,
    isUnitsCacheFresh,
    clearUnitsCache,
} from '../utils/userCache';

/**
 * Async thunk to fetch units with built-in request deduplication
 * and sessionStorage caching for instant display across page navigations.
 * 
 * Caching strategy (similar to Auth0/Firebase patterns):
 * 1. Check if data exists in Redux state (already loaded this session)
 * 2. Check if fresh cached data exists in sessionStorage
 * 3. Only make API call if no valid cache exists
 * 4. Cache is invalidated after 1 hour or on logout
 */
export const fetchUnits = createAsyncThunk(
  'units/fetchUnits',
  async (unitId, { rejectWithValue, getState }) => {
    try {
      const response = await axiosInstance.get('/api/units/');
      const units = response.data;
      const user = selectUserData(getState()); // Get current user data from state
      return { units, userId: user ? user.id : null, preselectedUnitId: unitId };
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || error.message || 'Failed to fetch units.';
      return rejectWithValue(errorMessage);
    }
  },
  {
    // Prevent duplicate requests - this runs BEFORE the thunk executes
    condition: (_, { getState }) => {
      const { units } = getState();
      // Only fetch if status is 'idle' (never fetched)
      // Skip if already loading or succeeded
      if (units.status === 'loading' || units.status === 'succeeded') {
        return false; // Cancel the thunk - don't execute
      }
      return true; // Proceed with the thunk
    },
  }
);

// Selector to get primary unitId (first selected) from unitsSlice
export const selectUnitId = (state) => state.units.unitId;

// Selector to get all selected unit ids
export const selectSelectedUnitIds = (state) => state.units.selectedUnitIds;

// Selector to get all units
export const selectUnits = (state) => state.units.units;

// Selector to get units fetch status
export const selectUnitsStatus = (state) => state.units.status;

// Get units led by the current user (memoized)
export const selectUnitsLedByUser = createSelector(
  [selectUserData, selectUnits],
  (user, units) => {
    if (!user) return [];
    return units.filter((unit) =>
      unit.unit_leaders && unit.unit_leaders.includes(user.id)
    );
  }
);

const findDefaultUnit = (units, userId) => {
  // Only select a unit if the user is a leader of at least one unit
  // First, try to find a unit where the user is a leader with visibility_level 1
  const userLedVisibleUnit = units.find(unit =>
    unit.unit_leaders?.includes(userId) && unit.visibility_level === 1
  );

  if (userLedVisibleUnit) return userLedVisibleUnit.id;

  // Try to find any unit where the user is a leader (with visibility_level 2 - Both)
  const userLedBothUnit = units.find(unit =>
    unit.unit_leaders?.includes(userId) && unit.visibility_level === 2
  );

  if (userLedBothUnit) return userLedBothUnit.id;

  // If user doesn't lead any unit, don't pre-select anything
  return null;
};

export const selectIsUserLeaderOfUnit = createSelector(
  [selectUserData, selectUnits, selectUnitId],
  (user, units, unitId) => {
    if (!user || !unitId || !units.length) return false;
    const selectedUnit = units.find(unit => unit.id === unitId);
    return selectedUnit?.unit_leaders?.includes(user.id) || false;
  }
);

// Selector to check leadership for an arbitrary unit id
// Uses String conversion to handle type mismatches (string vs number IDs)
export const selectIsUserLeaderOfUnitById = createSelector(
  [selectUserData, selectUnits, (_, unitId) => unitId],
  (user, units, unitId) => {
    if (!user || !unitId || !units.length) return false;
    const unit = units.find((u) => String(u.id) === String(unitId));
    return unit?.unit_leaders?.includes(user.id) || false;
  }
);

// Helper function to find the first unit with visibility_level equal to 1
const findFirstVisibleUnit = (units) => {
  return units.find((unit) => unit.visibility_level === 1);
};

/**
 * Initialize units state from sessionStorage cache.
 * Called once when the app loads to provide instant unit data
 * before any API calls complete.
 */
const getInitialState = () => {
  const cachedData = getCachedUnitsData();
  const isFresh = isUnitsCacheFresh();
  
  if (cachedData && isFresh && cachedData.units?.length > 0) {
    // We have fresh cached data - use it immediately
    return {
      unitId: cachedData.unitId,
      selectedUnitIds: cachedData.selectedUnitIds || [],
      units: cachedData.units,
      status: 'succeeded',
      error: null,
    };
  }
  
  // No cache or stale cache - start fresh
  return {
    unitId: null,
    selectedUnitIds: [],
    units: [],
    status: 'idle',
    error: null,
  };
};

const initialState = getInitialState();

// Store the default initial state for logout reset
const defaultState = {
  unitId: null,
  selectedUnitIds: [],
  units: [],
  status: 'idle',
  error: null,
};

const unitsSlice = createSlice({
  name: 'units',
  initialState,
  reducers: {
    setUnitId: (state, action) => {
      state.unitId = action.payload;
      state.selectedUnitIds = action.payload ? [action.payload] : [];
      // Update cache when selection changes
      cacheUnitsData(state.units, state.unitId, state.selectedUnitIds);
    },
    setSelectedUnitIds: (state, action) => {
      const ids = action.payload || [];
      state.selectedUnitIds = ids;
      state.unitId = ids.length ? ids[0] : null;
      // Update cache when selection changes
      cacheUnitsData(state.units, state.unitId, state.selectedUnitIds);
    },
    /**
     * Force a refresh of units data by resetting status to 'idle'.
     * Use this when you need to refetch data regardless of cache.
     */
    invalidateUnitsCache: (state) => {
      state.status = 'idle';
      clearUnitsCache();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUnits.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchUnits.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.units = action.payload.units;

        const { preselectedUnitId, userId, units } = action.payload;

        let resolvedUnitId = state.unitId;

        if (preselectedUnitId) {
          // If there's a preselected unitId from URL parameters, use it if valid
          const preselectedUnit = units.find(unit => unit.id === preselectedUnitId);
          if (preselectedUnit) {
            resolvedUnitId = preselectedUnitId;
          } else {
            console.warn(`Preselected unitId ${preselectedUnitId} not found.`);
            // Fall back to default selection
            resolvedUnitId = findDefaultUnit(units, userId);
          }
        } else if (!state.unitId) {
          // No preselected unit and no current selection
          resolvedUnitId = findDefaultUnit(units, userId);
        }

        state.unitId = resolvedUnitId;
        state.selectedUnitIds = resolvedUnitId ? [resolvedUnitId] : [];
        
        // Cache the fresh data to sessionStorage
        cacheUnitsData(state.units, state.unitId, state.selectedUnitIds);
      })
      .addCase(fetchUnits.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Reset state on logout and clear cache
      .addCase(logout, (state) => {
        state.unitId = defaultState.unitId;
        state.selectedUnitIds = defaultState.selectedUnitIds;
        state.units = defaultState.units;
        state.status = defaultState.status;
        state.error = defaultState.error;
        clearUnitsCache();
      });
  },
});

export const { setUnitId, setSelectedUnitIds, invalidateUnitsCache } = unitsSlice.actions;

export default unitsSlice.reducer;
